<template>
	<div class="t-horizontal-list">
		<div v-if="showLeftShadow" class="t-horizontal-list-left-shadow"/>

		<div v-if="hasScroll" class="t-horizontal-list-left">
			<t-icon-button
				v-if="showLeftShadow"
				appearance="primary"
				shape="circle"
				icon="chevron-left"
				type="bp"
				@click="navigateLeft()"
			/>
		</div>

		<div
			ref="list"
			class="t-horizontal-list-content"
			:style="cssStyle"
			@scroll.passive="_updateShadows()"
		>
			<span v-if="hasScroll" class="t-horizontal-list-content-spacer"/>
			<slot/>
			<span v-if="hasScroll" class="t-horizontal-list-content-spacer"/>
		</div>

		<div v-if="hasScroll" class="t-horizontal-list-right">
			<t-icon-button
				v-if="showRightShadow"
				appearance="primary"
				shape="circle"
				icon="chevron-right"
				type="bp"
				@click="navigateRight()"
			/>
		</div>

		<div v-if="showRightShadow" class="t-horizontal-list-right-shadow"/>
	</div>
</template>

<script>
export default {
	props: {
		gridColumnGap: String,
	},
	data() {
		return {
			hasScroll: false,
			showLeftShadow: false,
			showRightShadow: false,
			observer: null,
			listOffsetWidth:0,
			listClientWidth:0,
		};
	},
	computed: {
		cssStyle() {
			return { 'grid-column-gap': this.gridColumnGap };
		}
	},
	mounted() {
		let { list } = this.$refs;
		this._calculateHasScroll();
		this._updateShadows();
		this.observer = new MutationObserver(this._onListMutated);

		this.observer.observe(
			list,
			{ attributes: true, childList: true, characterData: true, subtree: true }
		);
	},

	beforeDestroy() {
		if (this.observer)
			this.observer.disconnect();
	},
	methods: {
		navigateLeft() {
			let { list } = this.$refs;
			let width = list.offsetWidth;
			list.scrollLeft -= width/2;
			this._updateShadows();
		},
		navigateRight() {
			let { list } = this.$refs;
			let width = list.offsetWidth;
			list.scrollLeft += width/2;
			this._updateShadows();
		},
		_onListMutated() {
			this._calculateHasScroll();
			this._updateShadows();
		},
		_calculateHasScroll() {
			let { list } = this.$refs;
			this.hasScroll = list.scrollWidth > list.clientWidth;
		},
		_updateShadows() {
			let { list } = this.$refs;
			this.showLeftShadow = list.scrollLeft !== 0;
			this.showRightShadow = list.scrollLeft !== (list.scrollWidth - list.clientWidth);
		}
	}
};
</script>

<style lang="scss" scoped>
.t-horizontal-list {
	position: relative;
	height: 100%;
	width: 100%;
	&-content {
		display: grid;
		grid-auto-columns: minmax(min-content, max-content);
		grid-auto-flow: column;
		height: 100%;
		width: 100%;
		overflow-x: auto;
		align-items: center;
		scroll-behavior: smooth;
		&-spacer {
			width: 1px;
			height: 100%;
		}
	}
	&-left, &-right {
		z-index: 3;
		position: absolute;
		display: flex;
		align-items: center;
		justify-content: center;
		background: transparent;
		height: 100%;
		width: 50px;
		top: 0;
		transition: 0.2s;
	}
	&-left {
		border-radius: 0 30px 30px 0;
		left:0;
	}

	&-right {
		border-radius: 30px 0 0 30px;
		right:0;
	}
	&-left-shadow, &-right-shadow {
		z-index: 2;
		position: absolute;
		top: 0;
		height: 100%;
		width: 10px;
	}
	&-left-shadow {
		background: linear-gradient(to right, rgba(0,0,0,.1), rgba(0,0,0,.0));
		left:0;
	}
	&-right-shadow {
		background: linear-gradient(to left, rgba(0,0,0,.1), rgba(0,0,0,.0));
		right:0;
	}
}
</style>

<docs>
```jsx
<div style="width:100%">
	<t-horizontal-list grid-column-gap="10px">
		<t-destinations-large style="width: 400px;">
				<template v-slot:from>
					<t-details-item label="from" primary="Terminal Tiller" secondary="7038 Tiller" />
					<t-details-item label="consignor" primary="Last Shipping AS" />
					<t-details-item label="DEPARTURE" primary="08.01.2019" />
				</template>
				<template v-slot:to>
					<t-details-item label="from" primary="Terminal Ålesund" secondary="6001 Ålesund" />
					<t-details-item label="consignee" primary="Timpex AS" />
					<t-details-item label="ARRIVAL " primary="22.01.2019" />
				</template>
			</t-destinations-large>
			<t-destinations-large style="width: 400px;">
				<template v-slot:from>
					<t-details-item label="from" primary="Terminal Tiller" secondary="7038 Tiller" />
					<t-details-item label="consignor" primary="Last Shipping AS" />
					<t-details-item label="DEPARTURE" primary="08.01.2019" />
				</template>
				<template v-slot:to>
					<t-details-item label="from" primary="Terminal Ålesund" secondary="6001 Ålesund" />
					<t-details-item label="consignee" primary="Timpex AS" />
					<t-details-item label="ARRIVAL " primary="22.01.2019" />
				</template>
			</t-destinations-large>
			<t-destinations-large style="width: 400px;">
				<template v-slot:from>
					<t-details-item label="from" primary="Terminal Tiller" secondary="7038 Tiller" />
					<t-details-item label="consignor" primary="Last Shipping AS" />
					<t-details-item label="DEPARTURE" primary="08.01.2019" />
				</template>
				<template v-slot:to>
					<t-details-item label="from" primary="Terminal Ålesund" secondary="6001 Ålesund" />
					<t-details-item label="consignee" primary="Timpex AS" />
					<t-details-item label="ARRIVAL " primary="22.01.2019" />
				</template>
			</t-destinations-large>
			<t-destinations-large style="width: 400px;">
				<template v-slot:from>
					<t-details-item label="from" primary="Terminal Tiller" secondary="7038 Tiller" />
					<t-details-item label="consignor" primary="Last Shipping AS" />
					<t-details-item label="DEPARTURE" primary="08.01.2019" />
				</template>
				<template v-slot:to>
					<t-details-item label="from" primary="Terminal Ålesund" secondary="6001 Ålesund" />
					<t-details-item label="consignee" primary="Timpex AS" />
					<t-details-item label="ARRIVAL " primary="22.01.2019" />
				</template>
			</t-destinations-large>

	</t-horizontal-list>
</div>
```
</docs>